import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useAppContext } from "../../../context/AuthContext";
import { ArticleController, ArticleControllerType } from "../../../controller/article/ArticleController";
import AuthData from "../../../model/AuthData";
import EnumReviewStatus from "../../../utils/enum/EnumReviewStatus";
import MessageType from "../../../utils/MessageType";
import {
  handleErrorMessage,
  isUserAssociateEditor,
  richTextStateFromHTML,
  richTextStateToHtml,
  richTextValue,
} from "../../../utils/Utils";

interface MakeDecisionButtonServiceType {
  mailSuggestion: any;
  openDialog: any;
  handleOpenDialog: any;
  handleCloseDialog: any;
  associateList: any;
  mailSuggestionAssociateSelect: any;
  setMailSuggestionAssociateSelect: any;
  sendMail: any;
  authData: AuthData | undefined;
  mailRef: any;
  commentRef: any;
}

export default function MakeDecisionButtonService({
  reviewStatus,
  article,
  editorDecision,
  message,
}: any): MakeDecisionButtonServiceType {
  const { showMessage, authData, setBlockUI } = useAppContext();
  const { id }: any = useParams();
  const {
    axiosGetMailSuggestionToAssociate,
    axiosPostAssignAssociate,
    axiosGetAssociateMail,
    axiosGetEditorMail,
    axiosPostAssociateMail,
    axiosPostEditorMail,
  }: ArticleControllerType = ArticleController();
  const [mailSuggestion, setMailSuggestion] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState(false);

  const [mailSuggestionAssociate, setMailSuggestionAssociate] = useState<any>();
  const [mailSuggestionAssociateSelect, setMailSuggestionAssociateSelect] = useState<any>();

  const [associateList, setAssociateList] = useState<any>();

  const history = useHistory();

  const mailRef = useRef<any>();
  const commentRef = useRef<any>();

  useEffect(() => {
    if (mailSuggestionAssociate) {
      setMailSuggestion(mailSuggestionAssociate.find((x: any) => x.user.id === mailSuggestionAssociateSelect.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailSuggestionAssociateSelect]);

  useEffect(() => {
    if (mailSuggestion?.text) {
      mailRef.current.set(richTextStateFromHTML(message ?? mailSuggestion?.text));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailSuggestion?.text]);

  const handleOpenDialog = () => {
    if (EnumReviewStatus.ASSOCIATE_EDITOR === reviewStatus) {
      getMailSuggestionToAssociate();
    } else {
      isUserAssociateEditor(authData) ? getAssociateMail() : getEditorMail();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setMailSuggestion(null);
    setOpenDialog(false);
  };

  const getMailSuggestionToAssociate = () => {
    axiosGetMailSuggestionToAssociate(id)
      .then((result: any) => {
        setMailSuggestionAssociate(result.data);
        const listAssociate = result.data.map((a: any) => ({
          id: a.user.id,
          name: `${a.fullName} (${a.user.email}) (--${a.activePapers}--) active papers`,
          email: a.user.email,
        }));
        setAssociateList(listAssociate);
        setMailSuggestionAssociateSelect(listAssociate[0]);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const getAssociateMail = () => {
    axiosGetAssociateMail(id, reviewStatus)
      .then((result: any) => {
        setMailSuggestion(result.data);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const getEditorMail = () => {
    axiosGetEditorMail(id, reviewStatus)
      .then((result: any) => {
        if (
          editorDecision &&
          article.editorAssignements?.length > 0 &&
          article.editorAssignements[0].editorDecisions?.length > 0
        ) {
          setMailSuggestion({
            ...result.data,
            text: article.editorAssignements[0].editorDecisions[0].decisionDescription,
          });
        } else {
          setMailSuggestion(result.data);
        }
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const sendMail = (values: any) => {
    if (EnumReviewStatus.ASSOCIATE_EDITOR === reviewStatus) {
      postAssignAssociate(values);
    } else {
      isUserAssociateEditor(authData) ? postAssociateMail(values) : postEditorMail(values);
      handleCloseDialog();
    }
  };

  const postAssignAssociate = (values: any) => {
    setBlockUI(true);
    const emailDto = {
      to: [mailSuggestionAssociateSelect.email].join(),
      subject: values.subject,
      text: richTextStateToHtml(richTextValue(mailRef)),
      from: authData?.user.email,
      replyTo: authData?.user.email,
      sentDate: new Date(),
    };
    axiosPostAssignAssociate(id, mailSuggestionAssociateSelect.id, emailDto)
      .then((result: any) => {
        handleCloseDialog();
        showMessage("Associate editor successfully assigned.", MessageType.SUCCESS);
        history.push("/toDoList");
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      })
      .finally(() => setBlockUI(false));
  };

  const postAssociateMail = (values: any) => {
    axiosPostAssociateMail(
      id,
      reviewStatus,
      richTextStateToHtml(richTextValue(mailRef)),
      richTextStateToHtml(richTextValue(commentRef))
    )
      .then((result: any) => {
        handleCloseDialog();
        history.push("/toDoList");
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const postEditorMail = (values: any) => {
    const emailDto = {
      to: values.to.length > 0 ? values.to.join() : null,
      subject: values.subject,
      text: richTextStateToHtml(richTextValue(mailRef)),
      from: authData?.user.email,
      replyTo: authData?.user.email,
      sentDate: new Date(),
    };
    axiosPostEditorMail(id, reviewStatus, emailDto)
      .then((result: any) => {
        handleCloseDialog();
        history.push("/toDoList");
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  return {
    mailSuggestion,
    openDialog,
    handleOpenDialog,
    handleCloseDialog,
    associateList,
    mailSuggestionAssociateSelect,
    setMailSuggestionAssociateSelect,
    sendMail,
    authData,
    mailRef,
    commentRef,
  };
}
export type { MakeDecisionButtonServiceType };
